import type {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

import { NumberInputCell } from '~/components/common/TableCellRenderer';

import { formatNumber } from '~/utils/common';

import type { TypeInputStatus } from '~/types/common.types';
import type { TypeReceivingLabelItem } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 입하 라벨 출력 모달 테이블
 */
const getReceiveLabelPrintModalColumnDefs = ({
  labelCountsStatus,
  onLabelCountChange,
  onLabelCountFocus,
  onGridReady,
  onFirstDataRendered,
}: {
  labelCountsStatus: Record<number, TypeInputStatus>;
  onLabelCountChange: (receivingRequestItemId: number, value: string) => void;
  onLabelCountFocus: (receivingRequestItemId: number) => void;
  onGridReady: (api: GridApi<TypeReceivingLabelItem>) => void;
  onFirstDataRendered: (api: GridApi<TypeReceivingLabelItem>) => void;
}) => {
  const gridOptions: GridOptions = {
    getRowId: (params) => params.data.receivingRequestItemId.toString(),
    columnTypes: {
      number: {
        cellStyle: { textAlign: 'right' },
        valueFormatter: ({ value }: ValueFormatterParams<TypeReceivingLabelItem>) => {
          return value !== null && value !== undefined ? formatNumber(value) : '-';
        },
      },
    },
    onGridReady: (event: GridReadyEvent<TypeReceivingLabelItem>) => onGridReady(event.api),
    onFirstDataRendered: (event: FirstDataRenderedEvent<TypeReceivingLabelItem>) => onFirstDataRendered(event.api),
  };

  const columnDefs: ColDef<TypeReceivingLabelItem>[] = [
    {
      headerName: '',
      valueFormatter: () => '',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 44,
    },
    {
      field: 'goodsCode',
      headerName: '상품코드',
      width: 140,
    },
    {
      field: 'goodsName',
      headerName: '상품명',
      width: 180,
    },
    {
      field: 'usePallet',
      headerName: '파렛트 사용여부',
      width: 114,
      valueGetter: ({ data }: ValueGetterParams<TypeReceivingLabelItem, TypeReceivingLabelItem['usePallet']>) => {
        if (!data) {
          return '-';
        }
        return data.usePallet ? '사용함' : '사용안함';
      },
    },
    {
      field: 'box',
      headerName: '수량(BOX)',
      width: 88,
      type: 'number',
    },
    {
      field: 'unitPerBox',
      headerName: '입수(BOX당)',
      width: 100,
      type: 'number',
    },
    {
      field: 'unit',
      headerName: '수량(EA)',
      width: 78,
      type: 'number',
    },
    {
      field: 'totalUnit',
      headerName: '총 수량(EA)',
      width: 94,
      type: 'number',
    },
    {
      field: 'label',
      headerName: '라벨 수',
      width: 96,
      minWidth: 96,
      cellRenderer: NumberInputCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeReceivingLabelItem, TypeReceivingLabelItem['label']>) => {
        if (!data) return {};

        return {
          value: data.label?.toString() || '',
          status: labelCountsStatus[data.receivingRequestItemId],
          onChange: (value: string) => {
            onLabelCountChange(data.receivingRequestItemId, value);
          },
          onFocus: () => {
            onLabelCountFocus(data.receivingRequestItemId);
          },
        };
      },
    },
  ];

  return { gridOptions, columnDefs };
};

export default getReceiveLabelPrintModalColumnDefs;
